import { useState, useCallback } from 'react';
import { Block, BlockEditorState } from '../types';
import { BlockService } from '../services';

/**
 * Reconstruit un état cohérent depuis un tableau plat de blocs.
 * Source de vérité unique : block.parentId
 *  - children[] est recalculé depuis parentId (on ne fait jamais confiance aux children entrants)
 *  - rootBlocks est dérivé des blocs sans parentId
 *  - Tri par order partout
 */
/** Infère le blockType depuis le contenu (rétrocompatibilité pour les blocs sans blockType) */
function inferBlockType(block: Block): 'titre' | 'prestation' {
  if (block.blockType) return block.blockType;
  return (block.content.prixUnitaire || block.content.quantite || block.content.unite || block.content.intitule)
    ? 'prestation'
    : 'titre';
}

function buildStateFromBlocks(blocks: Block[]): Pick<BlockEditorState, 'blocks' | 'rootBlocks'> {
  // 1. Indexer tous les blocs, enfants réinitialisés + blockType migré
  const blocksMap: Record<string, Block> = {};
  blocks.forEach(block => {
    blocksMap[block.id] = { ...block, children: [], blockType: inferBlockType(block) };
  });

  // 2. Reconstruire children depuis parentId
  blocks.forEach(block => {
    if (block.parentId && blocksMap[block.parentId]) {
      blocksMap[block.parentId].children.push(block.id);
    }
  });

  // 3. Trier les children par order
  Object.values(blocksMap).forEach(block => {
    block.children.sort((a, b) => (blocksMap[a]?.order ?? 0) - (blocksMap[b]?.order ?? 0));
  });

  // 4. Racines = blocs sans parentId, triés par order
  const rootBlocks = blocks
    .filter(b => !b.parentId)
    .sort((a, b) => a.order - b.order)
    .map(b => b.id);

  return { blocks: blocksMap, rootBlocks };
}

export const useBlockState = (initialBlocks: Block[] = []) => {
  const [state, setState] = useState<BlockEditorState>(() => {
    if (initialBlocks.length === 0) {
      return { blocks: {}, rootBlocks: [], selectedBlockId: undefined, editingBlockId: undefined };
    }
    return {
      ...buildStateFromBlocks(initialBlocks),
      selectedBlockId: undefined,
      editingBlockId: undefined
    };
  });

  // Synchroniser avec des blocs externes (ensemble ajouté, import, etc.)
  // Reconstruit entièrement depuis parentId — robuste aux désalignements
  const syncWithExternalBlocks = useCallback((externalBlocks: Block[]) => {
    setState(prev => ({
      ...prev,
      ...buildStateFromBlocks(externalBlocks)
    }));
  }, []);

  // Mettre à jour un bloc
  const updateBlock = useCallback((id: string, updates: Partial<Block>) => {
    setState(prevState => {
      const currentBlock = prevState.blocks[id];
      if (!currentBlock) return prevState;

      const updatedBlock = { ...currentBlock, ...updates };

      // Si parentId a changé, mettre à jour les children des parents concernés
      if (updates.parentId !== undefined && updates.parentId !== currentBlock.parentId) {
        const newBlocks = { ...prevState.blocks, [id]: updatedBlock };
        let newRootBlocks = [...prevState.rootBlocks];

        // Retirer de l'ancien parent
        if (currentBlock.parentId && newBlocks[currentBlock.parentId]) {
          newBlocks[currentBlock.parentId] = {
            ...newBlocks[currentBlock.parentId],
            children: newBlocks[currentBlock.parentId].children.filter(c => c !== id)
          };
        } else {
          newRootBlocks = newRootBlocks.filter(r => r !== id);
        }

        // Ajouter au nouveau parent
        if (updates.parentId && newBlocks[updates.parentId]) {
          const newParent = newBlocks[updates.parentId];
          newBlocks[updates.parentId] = {
            ...newParent,
            children: [...newParent.children, id]
              .sort((a, b) => (newBlocks[a]?.order ?? 0) - (newBlocks[b]?.order ?? 0))
          };
        } else if (!updates.parentId) {
          newRootBlocks = [...newRootBlocks, id]
            .sort((a, b) => (newBlocks[a]?.order ?? 0) - (newBlocks[b]?.order ?? 0));
        }

        return { ...prevState, blocks: newBlocks, rootBlocks: newRootBlocks };
      }

      return {
        ...prevState,
        blocks: { ...prevState.blocks, [id]: updatedBlock }
      };
    });
  }, []);

  // Ajouter un bloc
  const addBlock = useCallback((block: Block) => {
    setState(prevState => {
      const newBlocks = { ...prevState.blocks, [block.id]: { ...block, children: [] } };
      let newRootBlocks = [...prevState.rootBlocks];

      if (block.parentId && newBlocks[block.parentId]) {
        // Rattacher au parent
        newBlocks[block.parentId] = {
          ...newBlocks[block.parentId],
          children: [...newBlocks[block.parentId].children, block.id]
        };
      } else {
        // Bloc racine
        newRootBlocks = [...newRootBlocks, block.id];
      }

      return { ...prevState, blocks: newBlocks, rootBlocks: newRootBlocks };
    });
  }, []);

  // Supprimer un bloc
  const removeBlock = useCallback((id: string) => {
    setState(prevState => {
      const block = prevState.blocks[id];
      if (!block) return prevState;

      const newBlocks = BlockService.deleteBlock(prevState.blocks, id);

      // Retirer des racines si nécessaire
      const newRootBlocks = block.parentId
        ? prevState.rootBlocks
        : prevState.rootBlocks.filter(r => r !== id);

      // Retirer des children du parent
      if (block.parentId && newBlocks[block.parentId]) {
        newBlocks[block.parentId] = {
          ...newBlocks[block.parentId],
          children: newBlocks[block.parentId].children.filter(c => c !== id)
        };
      }

      return { ...prevState, blocks: newBlocks, rootBlocks: newRootBlocks };
    });
  }, []);

  // Obtenir les blocs dans l'ordre d'affichage (sections fermées = enfants exclus)
  const getBlocksInOrder = useCallback((): Block[] => {
    const validatedBlocks = BlockService.validateAndFixRelations(state.blocks, state.rootBlocks);
    return BlockService.getBlocksInOrder(validatedBlocks, state.rootBlocks);
  }, [state.blocks, state.rootBlocks]);

  /**
   * Réorganise des blocs de façon atomique (un seul setState).
   * @param blockIdsToMove   IDs à déplacer
   * @param flatTargetIndex  Index dans blocksInOrder où insérer
   * @param targetParentId   Nouveau parent (undefined = racine)
   * @param blocksInOrder    Liste visible à l'instant du drop
   */
  const reorderBlocks = useCallback((
    blockIdsToMove: string[],
    flatTargetIndex: number,
    targetParentId: string | undefined,
    blocksInOrder: Block[]
  ) => {
    setState(prevState => {
      // Copies fraîches (children vidés — on les recontruit depuis parentId)
      const newBlocks: Record<string, Block> = {};
      Object.values(prevState.blocks).forEach(b => {
        newBlocks[b.id] = { ...b, children: [] };
      });

      const draggedSet = new Set(blockIdsToMove);

      // Guard : si targetParentId est un descendant d'un bloc déplacé → cycle → abort
      if (targetParentId) {
        let current: string | undefined = targetParentId;
        while (current) {
          if (draggedSet.has(current)) return prevState;
          current = prevState.blocks[current]?.parentId;
        }
      }

      // Niveau du parent cible
      const parentLevel = targetParentId && newBlocks[targetParentId]
        ? newBlocks[targetParentId].level
        : -1;
      const newLevel = parentLevel + 1;

      // Mise à jour récursive des niveaux des descendants
      const updateChildLevels = (blockId: string, level: number) => {
        const orig = prevState.blocks[blockId];
        if (!orig) return;
        if (newBlocks[blockId]) {
          newBlocks[blockId] = { ...newBlocks[blockId], level };
        }
        orig.children.forEach(childId => updateChildLevels(childId, level + 1));
      };

      // Racines de la sélection : blocs dont le parent N'EST PAS dans la sélection.
      // Seuls ceux-ci changent de parent ; les autres gardent leur relation interne.
      const selectionRoots = blockIdsToMove.filter(id => {
        const parentId = prevState.blocks[id]?.parentId;
        return !parentId || !draggedSet.has(parentId);
      });

      // Changer parentId et level uniquement des racines de la sélection.
      // Les enfants sélectionnés conservent leur parentId existant ; leurs niveaux
      // sont mis à jour récursivement depuis la racine.
      selectionRoots.forEach(id => {
        if (!newBlocks[id]) return;
        newBlocks[id] = { ...newBlocks[id], parentId: targetParentId, level: newLevel };
        prevState.blocks[id]?.children.forEach(childId =>
          updateChildLevels(childId, newLevel + 1)
        );
      });

      // Tous les frères du parent cible (y compris ceux cachés dans des sections fermées)
      const allSiblings = Object.values(prevState.blocks)
        .filter(b => b.parentId === targetParentId && !draggedSet.has(b.id))
        .sort((a, b) => a.order - b.order);

      // Point d'insertion : nombre de frères visibles avant flatTargetIndex
      const visibleSiblingsBefore = blocksInOrder
        .slice(0, flatTargetIndex)
        .filter(b => b.parentId === targetParentId && !draggedSet.has(b.id));

      let insertionPoint = 0;
      if (visibleSiblingsBefore.length > 0) {
        const lastVisibleId = visibleSiblingsBefore[visibleSiblingsBefore.length - 1].id;
        const idx = allSiblings.findIndex(s => s.id === lastVisibleId);
        if (idx >= 0) insertionPoint = idx + 1;
      }

      // Nouvel ordre des frères — seulement les racines de la sélection,
      // les enfants sélectionnés restent sous leur parent dans la sélection.
      const newSiblingOrder = [
        ...allSiblings.slice(0, insertionPoint).map(b => b.id),
        ...selectionRoots,
        ...allSiblings.slice(insertionPoint).map(b => b.id)
      ];

      // Affecter les valeurs d'order
      newSiblingOrder.forEach((id, order) => {
        if (newBlocks[id]) {
          newBlocks[id] = { ...newBlocks[id], order };
        }
      });

      // Reconstruire children depuis parentId
      Object.values(newBlocks).forEach(b => {
        if (b.parentId && newBlocks[b.parentId]) {
          newBlocks[b.parentId].children.push(b.id);
        }
      });
      Object.values(newBlocks).forEach(b => {
        newBlocks[b.id].children.sort(
          (a, bId) => (newBlocks[a]?.order ?? 0) - (newBlocks[bId]?.order ?? 0)
        );
      });

      const newRootBlocks = Object.values(newBlocks)
        .filter(b => !b.parentId)
        .sort((a, b) => a.order - b.order)
        .map(b => b.id);

      return { ...prevState, blocks: newBlocks, rootBlocks: newRootBlocks };
    });
  }, []);

  return {
    state,
    updateBlock,
    addBlock,
    removeBlock,
    getBlocksInOrder,
    syncWithExternalBlocks,
    reorderBlocks
  };
};
